import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import {
  Bell,
  Check,
  CheckCheck,
  ShieldCheck,
  MessageSquare,
  Heart,
  Award,
  X,
  Trash2
} from 'lucide-react';

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'report_verified':
        return <ShieldCheck size={20} className="text-green-400" />;
      case 'comment':
        return <MessageSquare size={20} className="text-blue-400" />;
      case 'like':
        return <Heart size={20} className="text-red-400" />;
      case 'achievement':
        return <Award size={20} className="text-yellow-400" />;
      case 'verification_request':
        return <ShieldCheck size={20} className="text-purple-400" />;
      default:
        return <Bell size={20} className="text-gray-400" />;
    }
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Just now';

    const now = Date.now();
    const time = timestamp.toDate ? timestamp.toDate().getTime() : timestamp;
    const diff = now - time;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) return new Date(time).toLocaleDateString();
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-white/5 rounded-lg transition-colors"
      >
        <Bell size={20} className="text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[600px] glass-card rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Bell size={20} className="text-cyan-400" />
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[500px]">
            {notifications.length === 0 ? (
              <div className="p-12 text-center">
                <Bell size={48} className="mx-auto mb-4 text-gray-600" />
                <h4 className="text-lg font-semibold text-white mb-2">No notifications</h4>
                <p className="text-sm text-gray-400">You're all caught up!</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  getIcon={getNotificationIcon}
                  getRelativeTime={getRelativeTime}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-700 text-center">
              <Link
                to="/notifications"
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Notification Item Component
function NotificationItem({ notification, onClick, getIcon, getRelativeTime }) {
  const isUnread = !notification.read;

  return (
    <div
      onClick={onClick}
      className={`p-4 border-b border-gray-700/50 hover:bg-white/5 transition-colors cursor-pointer ${
        isUnread ? 'bg-cyan-500/5' : ''
      }`}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-1">
          {getIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isUnread ? 'text-white font-medium' : 'text-gray-300'}`}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500">
              {getRelativeTime(notification.createdAt)}
            </span>
            {isUnread && (
              <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
            )}
          </div>
        </div>

        {/* Link indicator */}
        {notification.postId && (
          <div className="flex-shrink-0 text-gray-600">
            →
          </div>
        )}
      </div>
    </div>
  );
}
